import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useNotification, InputField } from "@mmfestival/shared-components";
import {
  getFirestore,
  collection,
  onSnapshot,
  doc,
  query,
  orderBy,
  getDocs,
  getDoc,
} from "firebase/firestore";
import { format, parse } from "date-fns";
import { Copy, Trash2, Plus, Users, X, Ticket } from "lucide-react";
import { LuPlus, LuTags, LuTicket } from "react-icons/lu";

export const Vouchers = () => {
  const { user } = useAuth();
  const { showNotification } = useNotification();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const db = getFirestore();

  const [form, setForm] = useState({
    code: "",
    discountPercentage: "100",
    maxUsage: "",
    allowedCategories: "",
    expiredAt: "",
  });

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [usersWhoUsed, setUsersWhoUsed] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false); // To match AdminLayout modal style if needed, but here we render inline form for now or allow toggle

  // Load vouchers (Real-time)
  useEffect(() => {
    const q = query(collection(db, "vouchers"), orderBy("code"));
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => {
        const data = d.data();
        let dateValue = "";
        if (data.expiredAtHuman) {
          try {
            const parsed = parse(data.expiredAtHuman, "dd/MM/yy", new Date());
            dateValue = format(parsed, "yyyy-MM-dd");
          } catch (e) {
            console.warn("Invalid date format:", data.expiredAtHuman);
          }
        }
        return { id: d.id, ...data, _dateValue: dateValue };
      });
      setVouchers(list);
      setLoading(false);
    });
    return unsub;
  }, [db]);

  // Create voucher
  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) {
      showNotification("error", "Voucher code is required");
      return;
    }

    let expiredAtHuman = null;
    if (form.expiredAt) {
      expiredAtHuman = format(new Date(form.expiredAt), "dd/MM/yy");
    }

    try {
      const token = await user.getIdToken();
      await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/api/vouchers`,
          {
            code: form.code.toUpperCase(),
            discountPercentage: form.discountPercentage,
            maxUsage: form.maxUsage,
            allowedCategories: form.allowedCategories
              .split(",")
              .map((c) => c.trim())
              .filter(Boolean),
            expiresAt: expiredAtHuman,
            isActive: true,
            // eventId is not in form currently, can add if needed later
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
      );
      
      showNotification(
        "success",
        `Voucher ${form.code.toUpperCase()} berhasil dibuat!`
      );
      setForm({
        code: "",
        discountPercentage: "100",
        maxUsage: "",
        allowedCategories: "",
        expiredAt: "",
      });
      setIsModalOpen(false);
    } catch (err) {
      showNotification("error", err.response?.data?.message || "Gagal membuat voucher");
      console.error(err);
    }
  };

  const handleExpiryChange = async (code, isoDate) => {
    let expiredAtHuman = null;
    if (isoDate) {
      expiredAtHuman = format(new Date(isoDate), "dd/MM/yy");
    }
    
    try {
        const token = await user.getIdToken();
        await axios.put(
            `${import.meta.env.VITE_API_BASE_URL}/api/vouchers/${code}`,
            { expiredAtHuman },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification("success", "Tanggal kadaluarsa diperbarui");
    } catch (err) {
        showNotification("error", "Gagal memperbarui voucher");
        console.error(err);
    }
  };

  const toggleActive = async (code, current) => {
    try {
        const token = await user.getIdToken();
        await axios.put(
            `${import.meta.env.VITE_API_BASE_URL}/api/vouchers/${code}`,
            { isActive: !current },
            { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification("success", `Status voucher ${!current ? 'diaktifkan' : 'dinonaktifkan'}`);
    } catch (err) {
        showNotification("error", "Gagal mengubah status voucher");
        console.error(err);
    }
  }

  const deleteVoucher = async (code) => {
    if (!window.confirm(`Hapus voucher ${code} secara permanen?`)) return;
    try {
        const token = await user.getIdToken();
        await axios.delete(
            `${import.meta.env.VITE_API_BASE_URL}/api/vouchers/${code}`,
            { headers: { Authorization: `Bearer ${token}` } }
        );
        showNotification("success", `${code} dihapus`);
    } catch (err) {
        showNotification("error", "Gagal menghapus voucher");
        console.error(err);
    }
  };

  const getVoucherStatus = (v) => {
    if (v.maxUsage && (v.usageCount || 0) >= v.maxUsage) return "Usage Limit";

    if (v.expiredAtHuman) {
      try {
        const parsed = parse(v.expiredAtHuman, "dd/MM/yy", new Date());
        // Set time to end of day for fair comparison
        parsed.setHours(23, 59, 59, 999);
        if (parsed < new Date()) return "Inactive";
      } catch (e) {
        // ignore
      }
    }

    if (!v.isActive) return "Inactive";

    return "Active";
  };

  const CopyButton = ({ code }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <button
        onClick={handleCopy}
        className={`transition ${
          copied ? "text-green-600" : "text-slate-400 hover:text-indigo-600"
        }`}
        title={copied ? "Copied!" : "Copy Code"}
      >
        {copied ? (
          <span className="text-xs font-bold">Copied!</span>
        ) : (
          <Copy size={16} />
        )}
      </button>
    );
  };

  // Lihat pengguna voucher + ambil nomor HP dari users
  const showUsersWhoUsed = async (voucherCode) => {
    setSelectedVoucher(voucherCode);
    setLoadingUsers(true);
    setUsersWhoUsed([]);

    try {
      const usedByRef = collection(db, "vouchers", voucherCode, "usedBy");
      const q = query(usedByRef, orderBy("usedAt", "desc"));
      const snap = await getDocs(q);

      const users = [];

      for (const d of snap.docs) {
        const data = d.data();

        const userInfo = {
          name: data.namaLengkap || "Anonymous",
          email: data.email || "-",
          phone: "-",
          category: data.category || "-",
          eventId: data.eventId || "-",
          usedAt: data.usedAt?.toDate() || new Date(),
        };

        // Ambil nomor HP dari collection users jika ada uid
        if (data.uid) {
          try {
            const userDoc = await getDoc(doc(db, "users", data.uid));
            if (userDoc.exists()) {
              const userData = userDoc.data();
              if (userData.telepon) userInfo.phone = userData.telepon;
              if (userData.namaLengkap) userInfo.name = userData.namaLengkap;
              if (userData.email) userInfo.email = userData.email;
            }
          } catch (err) {
            console.warn("Failed to load user data:", data.uid);
          }
        }

        users.push(userInfo);
      }

      setUsersWhoUsed(users);
    } catch (err) {
      console.error("Error loading usedBy:", err);
      showNotification("error", "Gagal memuat data pengguna");
    } finally {
      setLoadingUsers(false);
    }
  };

  return (
    <div className="space-y-4 pb-10">
      {/* Header with Dashboard Style */}
      <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
         <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
            <LuTicket size={120} />
         </div>
         <div className="relative z-10">
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
               <div>
                  <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                     <LuTags size={14} /> Voucher Management
                  </p>
                   <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words">Voucher Management</h1>
                   <p className="text-slate-500 text-sm max-w-xl">
                       Manage discount codes and track their usage in real-time.
                   </p>
               </div>
               <div className="flex md:justify-end">
                  <button
                     onClick={() => setIsModalOpen(!isModalOpen)}
                     className="bg-slate-800 text-white px-4 py-2 rounded-xl hover:bg-slate-900 transition flex items-center gap-2 shadow-sm"
                  >
                     <LuPlus size={18} />
                     {isModalOpen ? "Close Form" : "Create New Voucher"}
                  </button>
               </div>
            </div>
         </div>
      </div>

      {/* Form Create (Collapsible) */}
      {isModalOpen && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
          <div className="flex justify-between items-center mb-6">
             <h2 className="text-lg font-bold text-gray-800">Buat Voucher Baru</h2>
             <button onClick={() => setIsModalOpen(false)}><X size={20} className="text-gray-400 hover:text-gray-600"/></button>
          </div>
          <form
            onSubmit={handleCreateVoucher}
            className="grid md:grid-cols-2 gap-6"
          >
            <InputField
              label="Kode Voucher"
              name="code"
              value={form.code}
              onChange={(e) =>
                setForm({ ...form, code: e.target.value.toUpperCase() })
              }
              placeholder="e.g. BMM45-001"
              required
              labelColor="text-gray-700"
              inputClassName="border-gray-300 focus:border-slate-400 focus:ring-slate-400"
            />
            <InputField
              label="Persentase Diskon (%)"
              name="discountPercentage"
              type="number"
              min="1"
              max="100"
              value={form.discountPercentage}
              onChange={(e) =>
                setForm({ ...form, discountPercentage: e.target.value })
              }
              labelColor="text-gray-700"
              inputClassName="border-gray-300 focus:border-slate-400 focus:ring-slate-400"
            />
            <InputField
              label="Max Penggunaan (Kosong = Unlimited)"
              name="maxUsage"
              type="number"
              min="1"
              value={form.maxUsage}
              onChange={(e) => setForm({ ...form, maxUsage: e.target.value })}
              placeholder="e.g. 50"
              labelColor="text-gray-700"
              inputClassName="border-gray-300 focus:border-slate-400 focus:ring-slate-400"
            />
            <InputField
              label="Kategori Khusus (pisahkan koma)"
              name="allowedCategories"
              value={form.allowedCategories}
              onChange={(e) =>
                setForm({ ...form, allowedCategories: e.target.value })
              }
              placeholder="e.g. 10K, Half Marathon"
              labelColor="text-gray-700"
              inputClassName="border-gray-300 focus:border-slate-400 focus:ring-slate-400"
            />
            <InputField
              label="Tanggal Kadaluarsa (Opsional)"
              name="expiredAt"
              type="date"
              value={form.expiredAt}
              onChange={(e) => setForm({ ...form, expiredAt: e.target.value })}
              labelColor="text-gray-700"
              inputClassName="border-gray-300 focus:border-slate-400 focus:ring-slate-400"
            />
            <div className="md:col-span-2 flex justify-end">
              <button
                type="submit"
                className="px-8 py-3 bg-slate-800 text-white rounded-xl hover:bg-slate-900 transition font-semibold flex items-center gap-2"
              >
                <Plus size={20} /> Simpan Voucher
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Voucher List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kode
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Diskon
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Penggunaan
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Kategori
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Expired
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aksi
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-12 text-gray-500"
                  >
                    <div className="flex justify-center items-center gap-2">
                        <div className="w-5 h-5 border-2 border-lime-600 border-t-transparent rounded-full animate-spin"></div>
                        Loading data...
                    </div>
                  </td>
                </tr>
              ) : vouchers.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-12 text-gray-500"
                  >
                    <div className="flex flex-col items-center justify-center">
                        <Ticket size={48} className="text-gray-300 mb-2"/>
                        <p>Belum ada voucher yang dibuat.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                vouchers.map((v) => {
                  const status = getVoucherStatus(v);
                  return (
                    <tr key={v.code} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 font-mono font-bold text-gray-800">
                        <div className="flex items-center gap-3">
                          <span className="bg-gray-100 px-2 py-1 rounded border border-gray-200">{v.code}</span>
                          <CopyButton code={v.code} />
                          <button
                            onClick={() => showUsersWhoUsed(v.code)}
                            className="text-gray-400 hover:text-blue-600"
                            title="Lihat pengguna"
                          >
                            <Users size={16} />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-600">{v.discountPercentage}%</td>
                      <td className="px-6 py-4 text-gray-600">
                        <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded text-xs font-medium border border-blue-100">
                           {v.usageCount || 0} / {v.maxUsage || "∞"}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {v.allowedCategories?.join(", ") || "-"}
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="date"
                          value={v._dateValue || ""}
                          onChange={(e) =>
                             handleExpiryChange(v.code, e.target.value)
                          }
                          className="text-xs border-none bg-transparent focus:ring-0 text-gray-500 cursor-pointer hover:bg-gray-100 rounded px-2 py-1"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <button
                        onClick={() => toggleActive(v.code, v.isActive)}
                        className={`px-3 py-1 rounded-full text-xs font-bold transition border ${
                          status === "Active"
                            ? "bg-green-100 text-green-800 border-green-200 hover:bg-green-200"
                            : status === "Usage Limit"
                            ? "bg-gray-100 text-gray-800 border-gray-200"
                            : "bg-red-100 text-red-800 border-red-200 hover:bg-red-200"
                        }`}
                      >
                        {status}
                      </button>
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => deleteVoucher(v.code)}
                          className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-full transition"
                          title="Hapus Voucher"
                        >
                          <Trash2 size={18} />
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Users */}
      {selectedVoucher && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl border border-gray-200 max-w-2xl w-full max-h-[80vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-gray-100 flex justify-between items-center sticky top-0 bg-white z-10">
              <h3 className="text-xl font-bold text-gray-800">
                Pengguna Voucher: <span className="text-lime-600 font-mono">{selectedVoucher}</span>
              </h3>
              <button
                onClick={() => setSelectedVoucher(null)}
                className="text-gray-400 hover:text-gray-600 transition"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-6">
                {loadingUsers ? (
                  <p className="text-center py-8 text-gray-500 animate-pulse">
                    Memuat data pengguna...
                  </p>
                ) : usersWhoUsed.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 flex flex-col items-center">
                      <Users size={48} className="text-gray-200 mb-2"/>
                      <p>Belum ada yang menggunakan voucher ini.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {usersWhoUsed.map((u, i) => (
                      <div
                        key={i}
                        className="border border-gray-200 rounded-xl p-4 bg-gray-50 hover:bg-white hover:shadow-sm transition"
                      >
                        <div className="flex justify-between items-start">
                            <div>
                                <div className="font-bold text-gray-800">{u.name}</div>
                                <div className="text-sm text-gray-600 mt-1">
                                  {u.category} • {u.eventId}
                                </div>
                            </div>
                            <div className="text-xs text-gray-400 font-mono bg-white px-2 py-1 rounded border border-gray-200">
                                {format(u.usedAt, "dd MMM yyyy HH:mm")}
                            </div>
                        </div>
                        
                        <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                             <div className="text-gray-500 flex items-center gap-1">
                                📧 {u.email}
                             </div>
                             <div className="text-gray-500 flex items-center gap-1">
                                📱 {u.phone || "-"}
                             </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
